import { BitmapText, Container, TextStyle, Text as PixiText } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi'
import { formatToCommaWithLang, soundManager, SoundType, useChipSetStore } from '@slot-nx/core'
import { playSpine, sleep } from '../../winning/utils/spinePlayer'

import { GodziDropSoundType, GodziType } from '../enum/godziDropEnum'
import { useGodziDropStore } from '../store/godziDropStore'
import { MachineActionType, sweetBombMachine } from '../../../../machine/sweetbombMachine'
import gsap from 'gsap'

const tapText = new PixiText('TAP ANYWHERE TO CONTINUE', new TextStyle({
  fontSize: 30,
  fill: '#ffffff',
  align: 'center',
}))
tapText.anchor.set(0.5)
tapText.x = 0
tapText.y = 500

export enum GodziDropAnimation {
  RatioL = 'animation1',
  FreeGameL = 'animation2',
  RatioV = 'animation3',
  FreeGameV = 'animation4',
  TotalWinL = 'animation_T1',
  TotalWinV = 'animation_T2',
  FreeSpinL = 'animation_freespin1',
  FreeSpinV = 'animation_freespin2',
  BetterLuckL = 'animation_betterLuck',
  BetterLuckV = 'animation_betterLuck2',
}

class GodziDropService {
  public godziWrapper: Container
  public multiplierText: BitmapText
  public payoutText: BitmapText
  private godziSpine: Spine
  private remainStakeIndex: number

  private exitGodziDrop() {
    this.multiplierText.text = ''
    this.payoutText.text = ''
    this.godziWrapper.removeChildren()
    this.godziSpine.destroy()
    soundManager.switchBGM(SoundType.bgm)
    soundManager.playBGM()
    useGodziDropStore.setState({ isShowGodziFire: false })
    if (useGodziDropStore.getState().godziResult.type !== GodziType.RatioPrize) {
      useChipSetStore.setState({ currentIndex: this.remainStakeIndex })
    }
    sweetBombMachine.send({ type: MachineActionType.godziDropEnd })
  }

  private startGodziSpin() {
    soundManager.playBGM()
    const effectiveStake = useGodziDropStore.getState().godziResult.stake
    const chipSet = useChipSetStore.getState().chipSet
    this.remainStakeIndex = useChipSetStore.getState().currentIndex
    const currentStakeIndex = chipSet.findIndex(chip => chip === effectiveStake)
    useChipSetStore.setState({ currentIndex: currentStakeIndex })
    useGodziDropStore.setState({ isShowGodziFire: true })
    this.godziWrapper.removeChildren()
    this.godziSpine.destroy()
    setTimeout(() => {
      sweetBombMachine.send({ type: MachineActionType.godziDropStart })
    }, 800)
  }

  public tapAnywhereAnimation(text: Text | BitmapText | PixiText) {
    const tl = gsap.timeline({
      repeat: Infinity,
      yoyo: true,
    })

    tl.fromTo(
      text,
      { alpha: 0.2 },
      {
        duration: 1,
        alpha: 1,
        ease: 'sine.inOut',
      },
    )
  }

  public async enterFreeGamePrize() {
    await this.playInitPrizeSound()

    this.godziSpine = Spine.from('godziSkel', 'godziAtlas')
    this.godziWrapper.addChild(this.godziSpine)
    const animationName = GodziDropAnimation.FreeGameV
    playSpine(this.godziSpine, animationName)

    await this.playWinPrizeSound()

    this.godziWrapper.addChild(tapText)
    this.tapAnywhereAnimation(tapText)

    this.godziWrapper.onpointertap = () => {
      this.startGodziSpin()
      this.godziWrapper.onpointertap = null
    }
  }

  public async enterFreeSpinPrize() {
    await this.playInitPrizeSound()
    this.godziSpine = Spine.from('godziSkel', 'godziAtlas')
    this.godziWrapper.addChild(this.godziSpine)
    const animationName = GodziDropAnimation.FreeSpinV
    playSpine(this.godziSpine, animationName)
    await this.playWinPrizeSound()

    this.godziWrapper.addChild(tapText)
    this.tapAnywhereAnimation(tapText)

    this.godziWrapper.onpointertap = () => {
      this.startGodziSpin()
      useGodziDropStore.setState({ isShowFreeSpinBtn: true })
      this.godziWrapper.onpointertap = null
    }
  }

  public async showRatioWin() {
    await this.playInitPrizeSound()

    this.godziSpine = Spine.from('godziSkel', 'godziAtlas')
    this.godziWrapper.addChild(this.godziSpine)
    const animationName = GodziDropAnimation.RatioV
    playSpine(this.godziSpine, animationName)
    await this.playWinPrizeSound()

    const { prize, payout } = useGodziDropStore.getState().godziResult
    this.multiplierText.text = `X${prize}`
    await sleep(1500)
    this.multiplierText.text = ''
    soundManager.play(GodziDropSoundType.godziVictory)
    const totalWinAnimation = GodziDropAnimation.TotalWinV
    playSpine(this.godziSpine, totalWinAnimation, true)

    this.payoutText.text = formatToCommaWithLang(payout)

    this.godziWrapper.addChild(tapText)
    this.tapAnywhereAnimation(tapText)

    const onEndCb = () => {
      this.exitGodziDrop()
      // usePlayerStore.setState({
      //   balance: preciseAdd(usePlayerStore.getState().balance, payout),
      // }) <-- Moved to machine addBackSettledBalance

      this.godziWrapper.onpointertap = null
    }
    this.godziWrapper.onpointertap = onEndCb
  }

  public async showFreeGamePrizeWin() {
    soundManager.stopBGM()
    soundManager.play(GodziDropSoundType.godziVictory)
    this.godziSpine = Spine.from('godziSkel', 'godziAtlas')
    this.godziWrapper.addChild(this.godziSpine)
    const animationName = GodziDropAnimation.TotalWinV
    playSpine(this.godziSpine, animationName, true)
    const godziPayout = useGodziDropStore.getState().godziResult.payout
    this.payoutText.text = formatToCommaWithLang(godziPayout)

    await sleep(1500)
    this.godziWrapper.addChild(tapText)
    this.tapAnywhereAnimation(tapText)

    const onEndCb = () => {
      this.exitGodziDrop()

      this.godziWrapper.onpointertap = null
    }
    this.godziWrapper.onpointertap = onEndCb
  }

  public async showFreeSpinPrizeWin() {
    soundManager.stopBGM()
    const godziPayout = useGodziDropStore.getState().godziResult.payout

    if (godziPayout > 0) {
      soundManager.play(GodziDropSoundType.godziVictory)
      this.godziSpine = Spine.from('godziSkel', 'godziAtlas')
      this.godziWrapper.addChild(this.godziSpine)
      const animationName = GodziDropAnimation.TotalWinV
      playSpine(this.godziSpine, animationName, true)
      this.payoutText.text = formatToCommaWithLang(godziPayout)
      await sleep(1500)
      this.godziWrapper.addChild(tapText)
      this.tapAnywhereAnimation(tapText)
      const onEndCb = () => {
        this.exitGodziDrop()
        this.godziWrapper.onpointertap = null
      }
      this.godziWrapper.onpointertap = onEndCb
    }
    else {
      this.godziSpine = Spine.from('godziBetterLuckSkel', 'godziBetterLuckAtlas')
      this.godziWrapper.addChild(this.godziSpine)
      const animationName = GodziDropAnimation.BetterLuckV
      playSpine(this.godziSpine, animationName, true)
      await sleep(1500)
      this.exitGodziDrop()
    }
    useGodziDropStore.setState({ isShowFreeSpinBtn: false, freeSpinCount: 0 })
  }

  private async playInitPrizeSound() {
    soundManager.stopBGM()
    soundManager.play(GodziDropSoundType.godziOpenEye)
    await sleep(1000)
  }

  private async playWinPrizeSound() {
    await sleep(2000)
    soundManager.play(GodziDropSoundType.godziRoar)
    soundManager.play(GodziDropSoundType.godziCoins)
    soundManager.play(GodziDropSoundType.godziCoinBgm)
    await sleep(4000)
    soundManager.play(GodziDropSoundType.godziShowPrize)
    await sleep(2000)
  }
}

export const godziDropService = new GodziDropService()
